import React from "react";
import { useI18n } from "../i18n";
import { recordError } from "../utils/clientErrors";
import ReportProblem from "./ReportProblem";
import SupportButton from "./SupportButton";
import { useMembership } from "../context/MembershipContext";

const CrashScreen = ({ compact }) => {
  const { t } = useI18n();
  const [reporting, setReporting] = React.useState(false);
  // Reporting needs a signed-in member; before that only the reload is offered
  const signedIn = Boolean(localStorage.getItem("token"));
  // The report endpoint only exists for shops it has been rolled out to
  const membership = useMembership();
  const canReport = signedIn && (membership ? Boolean(membership.flag?.("support_tools")) : true);

  return (
    <div
      role="alert"
      className={`${compact ? "py-10" : "min-h-screen flex items-center justify-center bg-gray-50"} px-4`}
    >
      <div className="max-w-md mx-auto text-center bg-white rounded-xl shadow p-6 space-y-4">
        <div className="text-4xl" aria-hidden="true">🛠️</div>
        <h1 className="text-xl font-bold text-gray-800">{t("support.errTitle")}</h1>
        <p className="text-gray-600">{t("support.errBody")}</p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-3 rounded-lg"
          >
            {t("support.errReload")}
          </button>
          {canReport && (
            <button
              type="button"
              onClick={() => setReporting(true)}
              className="border border-gray-300 text-gray-800 font-semibold px-4 py-3 rounded-lg hover:bg-gray-50"
            >
              {t("support.reportOpen")}
            </button>
          )}
          <SupportButton variant="full" />
          <a href="/admin" className="text-sm text-gray-500 hover:underline pt-1">
            {t("support.errHome")}
          </a>
        </div>
      </div>
      {reporting && <ReportProblem onClose={() => setReporting(false)} />}
    </div>
  );
};

/**
 * A screen that throws must not leave a blank page: the person sees what
 * happened, can reload, and can tell the operator in one tap — the error is
 * attached for them. `resetKey` clears it when they navigate elsewhere, so one
 * broken page does not take the whole dashboard down with it.
 */
class ErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    recordError(error, "screen");
    if (info?.componentStack) recordError(`${error?.message}\n${info.componentStack.split("\n").slice(0, 4).join("\n")}`, "screen");
  }

  componentDidUpdate(previous) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) return <CrashScreen compact={this.props.compact} />;
    return this.props.children;
  }
}

export default ErrorBoundary;
